import { Avatar, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { StatusBadge } from './StatusBadge';
import { OverrideWarning } from './OverrideWarning';
import { formatRelativeTime } from '../../../../utils/formatters';
import type { MatchingSessionListItem } from '../../../../services/api/matchingSessions';

interface MatchingSessionRowProps {
  session: MatchingSessionListItem;
  currentUserId: string | null;
  isResuming: boolean;
  onResume: (sessionId: string) => void;
  onViewGroups: () => void;
}

const ERROR_TRUNCATION_LIMIT = 80;

function truncateError(message: string | null): string {
  if (!message) return '';
  return message.length > ERROR_TRUNCATION_LIMIT
    ? `${message.slice(0, ERROR_TRUNCATION_LIMIT)}…`
    : message;
}

function ownerDisplayName(session: MatchingSessionListItem): string {
  const first = session.owner_first_name?.trim() ?? '';
  const last = session.owner_last_name?.trim() ?? '';
  const combined = `${first} ${last}`.trim();
  return combined.length > 0 ? combined : 'Unknown user';
}

function ownerInitials(session: MatchingSessionListItem): string {
  const first = session.owner_first_name?.charAt(0) ?? '';
  const last = session.owner_last_name?.charAt(0) ?? '';
  const combined = `${first}${last}`.toUpperCase();
  return combined.length > 0 ? combined : '?';
}

/**
 * Single row in the matching sessions panel (MED-829 / E23-8 AC8-AC10).
 *
 * Per-row action branches on (isOwnRow, status):
 * - own row + status in {ready, in_progress, awaiting_finalize, completed, failed}
 *   → Resume button (AC9)
 * - own row + status === 'submitting' → "Starting…" inline (AC9)
 * - other admin's row + completed → View groups (AC10)
 * - other admin's row + status in {in_progress, awaiting_finalize} → "in progress" inline (AC10)
 * - other admin's row + failed → just owner + truncated error (AC10)
 */
export function MatchingSessionRow({
  session,
  currentUserId,
  isResuming,
  onResume,
  onViewGroups,
}: MatchingSessionRowProps) {
  const isOwnRow = session.opened_by_admin_id === currentUserId;
  const showOverrideBadge = session.status === 'completed' && session.user_in_valid === true;
  const showFailedError = session.status === 'failed' && session.error_message !== null;

  const renderAction = () => {
    if (isOwnRow) {
      if (session.status === 'submitting') {
        return (
          <Text size="sm" c="dimmed" fs="italic">
            Starting…
          </Text>
        );
      }
      return (
        <Button
          size="sm"
          variant="light"
          loading={isResuming}
          onClick={() => onResume(session.id)}
          aria-label={`Resume matching session from ${formatRelativeTime(session.updated_at)}`}
        >
          Resume
        </Button>
      );
    }

    if (session.status === 'completed') {
      return (
        <Button
          size="sm"
          variant="subtle"
          onClick={onViewGroups}
          aria-label="View groups for this matching session"
        >
          View groups
        </Button>
      );
    }

    if (session.status === 'in_progress' || session.status === 'awaiting_finalize') {
      return (
        <Text size="sm" c="dimmed" fs="italic">
          In progress
        </Text>
      );
    }

    return null;
  };

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      align="flex-start"
      py="sm"
      px="md"
      style={{
        borderRadius: 8,
        border: '1px solid var(--mantine-color-gray-2)',
        backgroundColor: 'var(--mantine-color-white)',
      }}
    >
      <Group gap="md" wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }}>
        <StatusBadge status={session.status} />
        <Group gap="xs" wrap="nowrap" align="center">
          <Tooltip label={ownerDisplayName(session)} withArrow>
            <Avatar
              src={session.owner_avatar_url ?? undefined}
              radius="xl"
              size="sm"
              alt={ownerDisplayName(session)}
            >
              {ownerInitials(session)}
            </Avatar>
          </Tooltip>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {ownerDisplayName(session)}
            </Text>
            <Text size="xs" c="dimmed">
              {formatRelativeTime(session.updated_at)}
            </Text>
          </Stack>
        </Group>
        {showOverrideBadge && <OverrideWarning compact />}
        {showFailedError && (
          <Text size="xs" c="dangerRed" style={{ flexShrink: 1, minWidth: 0 }} truncate>
            {truncateError(session.error_message)}
          </Text>
        )}
      </Group>

      <div style={{ flexShrink: 0 }}>{renderAction()}</div>
    </Group>
  );
}
